import printIcon from "../../images/icons/print-icon.png"
import arrowLeftIcon from "../../images/icons/arrow-left-icon.png"
import { useHistory } from "react-router-dom";
import { useFirestoreID, useFirestoreGeneral, useFirestoreGeneralOrderBy } from '../../firebase/useFirestore';
import notDeleted from "../../helpers/notDeleted";
import flattenFieldsInOrder from "../../helpers/questionnaire/flattenFieldsInOrder";
import filterFieldsForMoment from "../../helpers/questionnaire/filterFieldsForMoment";
import scaleRange from "../../helpers/questionnaire/scaleRange";
import Location from '../../hooks/Location';
import { useEffect, useRef, useState } from "react";
import MultipleOptions from '../../components/Questionnaire/MultipleOptions';
import MatrixPrint from "../../components/Questionnaire/MatrixPrint";
import RequiredMark from "../../components/open/RequiredMark";
import ShowWhenNote from "../../components/Questionnaire/ShowWhenNote";
import { useTranslation } from "../../StateManagment/TranslationContext";
import buildTranslationStrings from "../../helpers/questionnaire/buildTranslationStrings";
import { client as tenant } from "../../hooks/Client";
import { buildShareQuestionnairePath } from "../../helpers/questionnaires/shareQuestionnairePath";
import scaleStartLabel from "../../helpers/questionnaire/scaleStartLabel";

// De eigen woorden van het printvel. De knoppen 'Terug' en 'Print vragenlijst'
// staan er bewust NIET bij: die zijn voor de medewerker achter het scherm en
// worden niet meegeprint.
const PRINT_STRINGS = {
    'Antwoord': 'Antwoord',
    'Overig...': 'Overig...',
    'Alleen invullen als': 'Alleen invullen als',
    'Deze vraag wordt altijd gesteld.': 'Deze vraag wordt altijd gesteld.',
}

const PrintQuestionaire = () => {
    // State
    const [questionnaire, setQuestionnaire] = useState('')
    const [client, setClient] = useState('')
    const [measureMomentID, setMeasureMomentID] = useState('')
    const [researchID, setResearchID] = useState('')
    const [persona, setPersona] = useState('')

    // Hooks
    const history = useHistory()

    // Url parameters
    const clientUrl = Location()[1]
    const questionnaireUrl = Location()[3]
    const measureMomentIDUrl = Location()[4]
    const researchIDUrl = Location()[5]
    const personaUrl = Location()[6]
    // De deellink draagt de taal mee als zevende segment. Ontbreekt hij, dan is
    // dit een oude link of een handmatig getypt pad: Nederlands.
    const languageCode = Location()[7] || 'nl'

    // Vertalingen
    const { translations, language, setTranslationInput } = useTranslation()

    // Firestore
    const questionnares = useFirestoreID('Questionnaires', questionnaire ? questionnaire : '123')
    const measureMoments = useFirestoreGeneral('MeasureMoments', 'ID', measureMomentID ? measureMomentID : '')
    const allFields = (useFirestoreGeneralOrderBy('QuestionnaireFields', 'QuestionnaireID', questionnaire ? questionnaire : '123', 'Position', 'asc') || []).filter(notDeleted)

    // Dezelfde vragen als het invulscherm: een geprinte lijst die niet klopt met
    // de digitale is erger dan geen print.
    const measureMoment = measureMoments?.[0]
    const fields = filterFieldsForMoment(allFields, measureMoment?.ExcludedFieldIDs)

    // Order fields by section: each top-level item, and right after a section its
    // questions in SectionPosition order. Section markers are skipped at render time.
    const orderedFields = flattenFieldsInOrder(fields)

    // Stabiele dependency-sleutels: een verse array per render zou de
    // vertaalronde eindeloos opnieuw starten.
    // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
    const questionnaireSig = (questionnares || []).map((item) => item?.ID).join(',')
    const fieldSig = orderedFields.map((field) => field?.ID).join(',')

    // Wie om Turks vroeg mag geen Nederlands vel uit de printer krijgen: dat is
    // achteraf niet te herstellen. `language` is de taal waar de huidige
    // vertalingen bij horen — verschilt die, dan loopt de ronde nog.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const translationPending = languageCode !== 'nl' && language !== languageCode

    // Wat er als laatste verstuurd is. ZONDER DEZE REM DRAAIT HET SCHERM VAST:
    // de provider maakt `setTranslationInput` als inline arrow, dus die heeft
    // bij elke render een nieuwe identiteit. Elke aanroep zet nieuwe state, dat
    // rendert de provider, dat geeft weer een nieuwe identiteit — 'Maximum
    // update depth exceeded'. Het invulscherm heeft dezelfde rem, om dezelfde
    // reden.
    const lastSentRef = useRef('')

    // Dezelfde set als het invulscherm, uit dezelfde bouwer: mist er hier een
    // tekst, dan staat er Nederlands tussen het Turks zonder dat iets het meldt.
    useEffect(() => {
        let cancelled = false
        const key = `${languageCode}|${questionnaireSig}|${fieldSig}`
        if (lastSentRef.current === key) return

        if (languageCode === 'nl') {
            lastSentRef.current = key
            setTranslationInput({ strings: {}, language: 'nl' })
            return
        }
        if (!questionnares || !orderedFields.length) return

        const send = async () => {
            const strings = await buildTranslationStrings({
                questionnaires: questionnares,
                fields: orderedFields,
                client: tenant,
                base: PRINT_STRINGS,
            })
            if (cancelled) return
            lastSentRef.current = key
            setTranslationInput({ strings, language: languageCode })
        }

        send()
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languageCode, questionnaireSig, fieldSig, setTranslationInput])

    useEffect(() => {
        setClient(clientUrl)
        setQuestionnaire(questionnaireUrl)
        setMeasureMomentID(measureMomentIDUrl)
        setResearchID(researchIDUrl)
        setPersona(personaUrl)
    }, [clientUrl, questionnaireUrl, measureMomentIDUrl, researchIDUrl, personaUrl])

  return (
    <div className="main">
        <div className="main-container">
            <div className="print-go-back-container" onClick={() => history.push(buildShareQuestionnairePath({ client, questionnaireID: questionnaire, researchID, respondentID: persona, measureMomentID }))}>
                <img src={arrowLeftIcon} alt=""/>
                <p>Terug</p>
            </div>
            <div className="print-icon-container" onClick={() => window.print()}>
                <img src={printIcon} alt="print icon" className="print-icon" />
                <p>Print vragenlijst</p>
            </div>
            <div id='print-questionniare-container'>
                {questionnares && questionnares.map(questionnaire => (
                    <div key={questionnaire.ID} className="questionnaire-container">
                        <h2>{translations?.[questionnaire.ID]?.title || questionnaire.Title}</h2>
                        <p>{translations?.[questionnaire.ID]?.description || questionnaire.Description}</p>
                    </div>
                ))}   
                {measureMoment?.Title &&
                    <p className="print-questionnaire-moment-note">Vragenlijst zoals gesteld op meetmoment '{measureMoment.Title}'</p>
                }
                <div id="print-questionnaire-fields-container">
                    {translationPending && <p className="print-questionaire-pending">De vragenlijst wordt vertaald…</p>}
                    {!translationPending && orderedFields.map((field, index) => (
                        field.Type === 'section' ? (
                            /* Een sectiekop is geen vraag: geen vraagblok eromheen, anders
                               staat er een leeg gat waar de kop hoort. */
                            <div key={field.ID} className="print-questionaire-section-container">
                                <h2>{translations?.[field.ID] || field.Question}</h2>
                                {field.Explainer && <p>{translations?.[`${field.ID}_explainer`] || field.Explainer}</p>}
                            </div>
                        ) : (
                        <div key={field.ID} className="questionnaire-field-container questionaire-field-print-container">
                            <div>
                                    <h3>{translations?.[field.ID] || field.Question}<RequiredMark required={field.Required} /></h3>
                                    <p>{translations?.[`${field.ID}_explainer`] || field.Explainer}</p>

                                    {/* Papier vertakt niet: de voorwaarde moet erbij,
                                        anders vult iemand een vraag in die hij digitaal
                                        nooit gekregen zou hebben. */}
                                    <ShowWhenNote field={field} />

                                    {/* Paragraph */}
                                    {field.Type === 'paragraph' &&
                                        <div className='questionnaire-field-type-paragraph-container print-questionaire-paragraph-container'>
                                            <p className='questionnaire-field-type-text' data-index={index}>{translations?.['Antwoord'] || 'Antwoord'}</p>
                                        </div>
                                    }

                                    {/* Number */}
                                    {field.Type === 'number' &&
                                        <div className='print-questionaire-number-container' data-index={index}></div>
                                    }

                                    {/* Scale */}
                                    {field.Type === 'scale' &&
                                        <div id='scale-container'>
                                            {translations?.[scaleStartLabel(field)] || scaleStartLabel(field)}
                                            {scaleRange(field).map(btn => (
                                            <div key={`${field.ID}-${btn}`} id='question-type-label-container' data-index={index}>
                                                    <input type="radio" value={btn} readOnly />
                                                    <label htmlFor={btn}>{btn}</label>
                                            </div>
                                            ))}
                                            {translations?.[field.ReachEndLabel] || field.ReachEndLabel}
                                        </div>
                }

                                    {/* Multiple choice */}
                                    {(field.Type === 'multiple-one' || field.Type === 'multiple-multiple') &&
                                     <MultipleOptions fieldID={field.ID} fieldType={field.Type}/>
                                    }

                                    {/* Matrix */}
                                    {(field.Type === 'matrix-one' || field.Type === 'matrix-multiple') &&
                                        <MatrixPrint field={field}/>
                                    }

                            </div>
                        </div>
                        )
                    ))}
                </div>
            </div>
        </div>
    </div>
  )
}

export default PrintQuestionaire